import type { TmVariant } from "@models/mappingResult";

/** Positional columns in historical TM keys and fields in new TM variants. */
export const TM_VARIANT_FIELDS = [
  "manufacturer", "model", "series_code", "model_year", "model_code",
  "engine_code", "displacement_cc", "fuel", "drive", "body", "power_kw",
  "power_hp", "cylinders", "valves", "engine_type", "fuel_mixture",
  "vehicle_type",
] as const;

/** A canonical key for option selection, deduplication and adjacent row grouping. */
export const variantIdentity = (variant: TmVariant): string => {
  if (typeof variant === "string") {
    const parts = variant.split("|");
    return parts.length === 9 || parts.length === 17 || parts.length === 19
      ? JSON.stringify(parts.slice(0, 17).map((part) => part.trim()).concat(Array(Math.max(0, 17 - parts.length)).fill("")))
      : JSON.stringify([variant]);
  }
  return JSON.stringify(TM_VARIANT_FIELDS.map((field) => variant[field]?.trim() ?? ""));
};

/** Readable display for structured and historical variants, omitting blank fields. */
export const formatVariantLabel = (variant: TmVariant): string =>
  (typeof variant === "string"
    ? variant.split("|")
    : TM_VARIANT_FIELDS.map((field) => variant[field] ?? ""))
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .join("|");


/** The six fields a TM vehicle is grouped by everywhere: the row review, Compare
 *  and the Excel export (mapping service ``batch_export.KEY_COLUMNS``). The other
 *  eleven describe the vehicle but never split a group. */
export const TM_VEHICLE_KEY_FIELDS = [
  { label: "Model code", field: "model_code" },
  { label: "Engine code", field: "engine_code" },
  { label: "Displacement (cc)", field: "displacement_cc" },
  { label: "Fuel", field: "fuel" },
  { label: "Cylinders", field: "cylinders" },
  { label: "Engine type", field: "engine_type" },
] as const;

/** A variant's six key values in ``TM_VEHICLE_KEY_FIELDS`` order; all blank for
 *  no variant; ``null`` for a free-text legacy label that has no fields. */
export const tmVehicleKeyValues = (variant: TmVariant | null): string[] | null => {
  if (variant === null) return TM_VEHICLE_KEY_FIELDS.map(() => "");
  if (typeof variant === "string") {
    const parts = variant.split("|");
    if (parts.length !== 9 && parts.length !== 17 && parts.length !== 19) {
      return variant.trim() ? null : TM_VEHICLE_KEY_FIELDS.map(() => "");
    }
    return TM_VEHICLE_KEY_FIELDS.map(({ field }) =>
      (parts[TM_VARIANT_FIELDS.indexOf(field)] ?? "").trim());
  }
  return TM_VEHICLE_KEY_FIELDS.map(({ field }) => variant[field]?.trim() ?? "");
};

/** The group key of a TM row's vehicle; ``""`` when the row has no vehicle.
 *  A free-text legacy label stays its own vehicle. */
export const tmVehicleKey = (variant: TmVariant | null): string => {
  const values = tmVehicleKeyValues(variant);
  if (values === null) return JSON.stringify([[], String(variant).trim()]);
  return values.some(Boolean) ? JSON.stringify([values, ""]) : "";
};

/** "Model code: CLA180, Fuel: petrol" — the key fields a vehicle prints; a
 *  free-text legacy label as is. */
export const formatTmVehicleKey = (variant: TmVariant | null): string => {
  const values = tmVehicleKeyValues(variant);
  if (values === null) return String(variant).trim();
  return TM_VEHICLE_KEY_FIELDS
    .map(({ label }, index) => (values[index] ? `${label}: ${values[index]}` : ""))
    .filter(Boolean)
    .join(", ");
};
